import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Save, Loader2, AlertCircle, X, PackagePlus, Check, AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { ProductAutocomplete } from './forms/ProductAutocomplete';
import { WarrantyDropdown } from './forms/WarrantyDropdown';
import { PaymentAllocationPicker } from './forms/PaymentAllocationPicker';
import { NoteAllocationPicker } from './forms/NoteAllocationPicker';
import { recalculateLineItem } from './lineItemCalculations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Lakshadweep', 'Puducherry'
];

const emptyLineItem = () => ({
  key: Date.now() + Math.random(),
  product_id: '',
  serial_number: '',
  description: '',
  mrp: '',
  quantity: 1,
  unit_id: '',
  price_per_unit: '',
  discount_percentage: 0,
  discount_amount: 0,
  tax_id: '',
  tax_amount: 0,
  total_amount: 0,
  warranty_days: '',
  warranty_id: '',
  warranty_name: '',
  part_sale_amount: '',
  _is_part_sale: false,
  _is_full_sale: false,
  _part_sale_unit_id: '',
  _non_stock_product: false,
  _stock_count: null,
  _sold_item_ids: [],
  _part_sale_allocations: [], // [{item_id, amount}]
  _items_confirmed: false,
  _from_dc: false,
});

export const SaleInvoiceCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const fromProforma = location.state?.fromProforma || null;
  const fromEstimate = location.state?.fromEstimate || null;
  const fromSource = fromProforma || fromEstimate || null;
  const fromDcId = searchParams.get('from_dc');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sourceDcId, setSourceDcId] = useState(fromDcId || null);
  const [sourceProformaId] = useState(fromProforma?.source_proforma_id || null);
  const [sourceProformaNumber] = useState(fromProforma?.source_proforma_number || '');
  const [sourceEstimateId] = useState(fromEstimate?.source_estimate_id || null);
  const [sourceEstimateNumber] = useState(fromEstimate?.source_estimate_number || '');

  // Master data
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [units, setUnits] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [warranties, setWarranties] = useState([]);
  const [users, setUsers] = useState([]);
  const [availablePayments, setAvailablePayments] = useState([]);
  const [paymentAllocations, setPaymentAllocations] = useState({});
  const [availableCreditNotes, setAvailableCreditNotes] = useState([]);
  const [creditNoteAllocations, setCreditNoteAllocations] = useState({});

  // Header
  const [saleType, setSaleType] = useState('Cash');
  const [customerId, setCustomerId] = useState(fromSource?.customer_id || '');
  const [alternateNumber, setAlternateNumber] = useState('');
  const [referralUserId, setReferralUserId] = useState('');
  const [companyId, setCompanyId] = useState(fromSource?.company_id || '');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(fromSource?.invoice_date || new Date().toISOString().split('T')[0]);
  const [stateOfSupply, setStateOfSupply] = useState(fromSource?.state_of_supply || '');
  const [lutArnNumber, setLutArnNumber] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [paymentTermId, setPaymentTermId] = useState('');
  const [paymentDueDate, setPaymentDueDate] = useState('');
  const [calculateMode, setCalculateMode] = useState('Excluding Tax');

  // Line items
  const [lineItems, setLineItems] = useState(
    fromSource?.line_items?.length
      ? fromSource.line_items.map(li => ({
          ...emptyLineItem(),
          product_id: li.product_id || '',
          description: li.description || '',
          mrp: li.mrp || '',
          quantity: li.quantity || 1,
          unit_id: li.unit_id || '',
          price_per_unit: li.price_per_unit || '',
          discount_percentage: li.discount_percentage || 0,
          discount_amount: li.discount_amount || 0,
          tax_id: li.tax_id || '',
          tax_amount: li.tax_amount || 0,
          total_amount: li.total_amount || 0,
          warranty_days: li.warranty_days || '',
          warranty_id: li.warranty_id || '',
          warranty_name: li.warranty_name || '',
        }))
      : [emptyLineItem()]
  );

  // Item selection modal
  const [itemModalIdx, setItemModalIdx] = useState(null);
  const [itemModalIds, setItemModalIds] = useState([]);
  const [itemModalAvailable, setItemModalAvailable] = useState([]);
  const [itemModalErrors, setItemModalErrors] = useState([]);
  const [itemModalValidating, setItemModalValidating] = useState(false);

  // Payment
  const [payments, setPayments] = useState([{ key: 1, payment_type: 'Cash', amount: 0 }]);
  const [description, setDescription] = useState(fromSource?.description || '');
  const [roundOff, setRoundOff] = useState(fromSource?.round_off !== undefined ? fromSource.round_off : true);
  const [isReceived, setIsReceived] = useState(false);
  const [receivedAmount, setReceivedAmount] = useState('');

  // Tax recalculation modal
  const [showTaxRecalcModal, setShowTaxRecalcModal] = useState(false);
  const [recalcPreview, setRecalcPreview] = useState([]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const [customersRes, companiesRes, productsRes, taxesRes, unitsRes, ptRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/customers`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/companies`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/taxes`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/units`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/payment-terms`, getAuthHeaders())
        ]);
        setCustomers(customersRes.data.filter(c => c.active));
        setCompanies(companiesRes.data.filter(c => c.active));
        setProducts(productsRes.data);
        setTaxes(taxesRes.data.filter(t => t.active));
        setUnits(unitsRes.data.filter(u => u.active));
        setPaymentTerms(ptRes.data.filter(p => p.active));
        const pmRes = await axios.get(`${BACKEND_URL}/api/payment-modes`, getAuthHeaders());
        setPaymentModes(pmRes.data.filter(p => p.active));
        const wRes = await axios.get(`${BACKEND_URL}/api/warranties`, getAuthHeaders());
        setWarranties(wRes.data || []);
        const usersRes = await axios.get(`${BACKEND_URL}/api/users`, getAuthHeaders());
        setUsers((usersRes.data || []).filter(u => u.active !== false));
        const txnRes = await axios.get(`${BACKEND_URL}/api/transactions?type=payment_in`, getAuthHeaders());
        setAvailablePayments(txnRes.data || []);
        const notesRes = await axios.get(`${BACKEND_URL}/api/credit-notes`, getAuthHeaders());
        setAvailableCreditNotes(notesRes.data || []);
      } catch (err) {
        setError('Failed to load master data');
      }
    };
    fetchMasterData();
  }, []);

  const useCreditNotes = payments.some(payment => payment.payment_type === 'Credit Note');

  useEffect(() => {
    setPaymentAllocations({});
    setCreditNoteAllocations({});
  }, [customerId, companyId]);

  useEffect(() => {
    if (!useCreditNotes) setCreditNoteAllocations({});
  }, [useCreditNotes]);

  // Auto-calculate payment due date from invoice date + payment term days
  useEffect(() => {
    if (!paymentTermId || !invoiceDate) { setPaymentDueDate(''); return; }
    const term = paymentTerms.find(pt => pt.id === paymentTermId);
    if (term?.days) {
      const base = new Date(invoiceDate);
      base.setDate(base.getDate() + term.days);
      setPaymentDueDate(base.toISOString().split('T')[0]);
    } else {
      setPaymentDueDate('');
    }
  }, [paymentTermId, invoiceDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // For pre-populated line items (from Proforma/Estimate conversion), fetch stock counts
  useEffect(() => {
    if (!fromSource?.line_items?.length) return;
    fromSource.line_items.forEach((li, idx) => {
      if (!li.product_id) return;
      axios.get(`${BACKEND_URL}/api/inventory/${li.product_id}/available-items`, getAuthHeaders())
        .then(res => {
          setLineItems(prev => {
            const updated = [...prev];
            if (updated[idx]) {
              updated[idx] = {
                ...updated[idx],
                _stock_count: res.data.stock_count,
              };
            }
            return updated;
          });
        }).catch(() => {});
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!fromDcId) return;
    try {
      const raw = sessionStorage.getItem('dc_sale_prefill');
      if (!raw) return;
      const prefill = JSON.parse(raw);
      sessionStorage.removeItem('dc_sale_prefill');
      setSourceDcId(prefill.source_dc_id || fromDcId);
      if (prefill.customer_id) setCustomerId(prefill.customer_id);
      if (prefill.company_id) setCompanyId(prefill.company_id);
      if (prefill.state_of_supply) setStateOfSupply(prefill.state_of_supply);
      if (prefill.description) setDescription(prefill.description);
      if (prefill.line_items?.length) {
        const items = prefill.line_items.map(li => ({
          ...emptyLineItem(),
          product_id: li.product_id || '',
          description: li.description || '',
          mrp: li.mrp || '',
          quantity: li.quantity || 1,
          unit_id: li.unit_id || '',
          price_per_unit: li.price_per_unit || '',
          discount_percentage: li.discount_percentage || 0,
          discount_amount: li.discount_amount || 0,
          tax_id: li.tax_id || '',
          tax_amount: li.tax_amount || 0,
          total_amount: li.total_amount || 0,
          warranty_days: li.warranty_days || '',
          warranty_id: li.warranty_id || '',
          warranty_name: li.warranty_name || '',
          _sold_item_ids: li.sold_item_ids || [],
          _items_confirmed: true,
          _stock_count: li.sold_item_ids?.length || Number(li.quantity || 0),
          _from_dc: true,
        }));
        items.push(emptyLineItem());
        setLineItems(items);
      }
    } catch { /* ignore parse errors */ }
  }, [fromDcId]);

  // Resolve converted/prefilled lines once product master data is available.
  useEffect(() => {
    if (!products.length) return;
    setLineItems(prev => prev.map(item => {
      if (!item.product_id) return item;
      const isNonStock = Boolean(products.find(product => product.id === item.product_id)?.non_stock_product);
      if (!isNonStock && item._non_stock_product === false) return item;
      return {
        ...item,
        _non_stock_product: isNonStock,
        ...(isNonStock ? {
          _stock_count: null,
          _sold_item_ids: [],
          _part_sale_allocations: [],
          _is_part_sale: false,
          _items_confirmed: true,
        } : {}),
      };
    }));
  }, [products]);

  useEffect(() => {
    setPaymentAllocations({});
  }, [customerId, companyId]);


  // Auto-fill billing address when customer changes
  useEffect(() => {
    if (customerId) {
      const cust = customers.find(c => c.id === customerId);
      if (cust && cust.billing_address) {
        setBillingAddress(cust.billing_address);
      }
    }
  }, [customerId, customers]);

  // Auto-fill invoice number from config when company changes
  useEffect(() => {
    if (!companyId) return;
    axios.get(`${BACKEND_URL}/api/invoice-config/${companyId}/next-number`, getAuthHeaders())
      .then(res => {
        if (res.data.invoice_number) {
          setInvoiceNumber(res.data.invoice_number);
        }
      }).catch(() => {});
  }, [companyId]);

  // Re-fetch stock counts when company changes
  useEffect(() => {
    lineItems.forEach(li => {
      if (!li.product_id) return;
      if (li._non_stock_product) return;
      // Items supplied by a Delivery Challan are reserved for that DC and are
      // intentionally absent from normal in-stock availability.
      if (sourceDcId && li._from_dc) return;
      const companyParam = companyId ? `?company_id=${companyId}` : '';
      axios.get(`${BACKEND_URL}/api/inventory/${li.product_id}/available-items${companyParam}`, getAuthHeaders())
        .then(res => {
          setLineItems(p => {
            const u = [...p];
            const found = u.find(i => i.key === li.key);
            if (found) {
              found._stock_count = res.data.stock_count;
              found._items_confirmed = false;
              found._sold_item_ids = [];
              found._part_sale_allocations = [];
            }
            return u;
          });
        }).catch(() => {});
    });
  }, [companyId, sourceDcId]);


  const recalcLine = (item, changedField = null) => recalculateLineItem(item, taxes, changedField);

  const updateLineItem = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'product_id') {
        const prod = products.find(p => p.id === value);
        const isNonStock = Boolean(prod?.non_stock_product);
        if (prod) {
          if (prod.is_part_sale) {
            // Default to part sale mode: use part_sale_unit_id
            updated[index].unit_id = prod.part_sale_unit_id || prod.unit_id;
            updated[index]._part_sale_unit_id = prod.part_sale_unit_id || '';
            updated[index]._is_full_sale = false;
          } else {
            updated[index].unit_id = prod.unit_id || '';
            updated[index]._part_sale_unit_id = '';
            updated[index]._is_full_sale = false;
          }
        }
        updated[index]._non_stock_product = isNonStock;
        updated[index]._is_part_sale = !isNonStock && Boolean(prod?.is_part_sale);
        updated[index].part_sale_amount = '';
        updated[index]._sold_item_ids = [];
        updated[index]._items_confirmed = isNonStock;
        updated[index]._stock_count = null;
        // Fetch stock count (filtered by selected company)
        if (value && !isNonStock) {
          const companyParam = companyId ? `?company_id=${companyId}` : '';
          axios.get(`${BACKEND_URL}/api/inventory/${value}/available-items${companyParam}`, getAuthHeaders())
            .then(res => {
              setLineItems(p => {
                const u = [...p];
                const li = u.find(i => i.key === updated[index].key);
                if (li) {
                  li._stock_count = res.data.stock_count;
                }
                return u;
              });
            }).catch(() => {});
        }
        if (value && !updated[updated.length - 1]?.product_id) return updated.map(i => recalcLine(i));
        if (value) updated.push(emptyLineItem());
      }
      if (field === 'quantity') {
        updated[index]._sold_item_ids = [];
        updated[index]._part_sale_allocations = [];
        updated[index]._items_confirmed = updated[index]._non_stock_product;
      }
      if (field === '_is_full_sale') {
        const prod = products.find(p => p.id === updated[index].product_id);
        if (value) {
          // Full sale: use product unit, clear part sale amount
          updated[index].unit_id = prod?.unit_id || '';
          updated[index].part_sale_amount = '';
        } else {
          // Part sale: use part sale unit
          updated[index].unit_id = prod?.part_sale_unit_id || prod?.unit_id || '';
        }
      }
      updated[index] = recalcLine(updated[index], field);
      return updated;
    });
  };

  const addLineItem = () => setLineItems(prev => [...prev, emptyLineItem()]);

  const removeLineItem = (index) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  // Item selection modal
  const openItemModal = async (idx) => {
    const li = lineItems[idx];
    if (!li.product_id || li._non_stock_product) return;
    const qty = parseInt(li.quantity) || 1;
    setItemModalIdx(idx);
    setItemModalErrors([]);
    try {
      const companyParam = companyId ? `?company_id=${companyId}` : '';
      const res = await axios.get(`${BACKEND_URL}/api/inventory/${li.product_id}/available-items${companyParam}`, getAuthHeaders());
      setItemModalAvailable(res.data.items || []);
    } catch { setItemModalAvailable([]); }

    if (li._is_part_sale) {
      // Part sale: show allocation rows (item_id + amount)
      if (li._part_sale_allocations.length > 0) {
        setItemModalIds(li._part_sale_allocations.map(a => `${a.item_id}||${a.amount}`));
      } else {
        setItemModalIds(['||']);
      }
    } else {
      // Full sale: N item ID fields
      setItemModalIds(li._sold_item_ids.length === qty ? [...li._sold_item_ids] : Array(qty).fill(''));
    }
  };

  const validateAndConfirmItems = async () => {
    const li = lineItems[itemModalIdx];

    if (li._is_part_sale) {
      // Part sale validation: parse allocation rows
      const allocations = itemModalIds.map(row => {
        const [itemId, amt] = row.split('||');
        return { item_id: (itemId || '').trim(), amount: parseFloat(amt) || 0 };
      }).filter(a => a.item_id);

      if (allocations.length === 0) {
        setItemModalErrors(['Please add at least one item ID with amount']);
        return;
      }
      const totalAllocated = allocations.reduce((s, a) => s + a.amount, 0);
      const qty = parseFloat(li.quantity) || 0;
      if (Math.abs(totalAllocated - qty) > 0.01) {
        setItemModalErrors([`Total allocated (${totalAllocated}) must equal quantity (${qty})`]);
        return;
      }
      // Validate item IDs exist and check remaining qty
      const ids = allocations.map(a => a.item_id);
      setItemModalValidating(true);
      setItemModalErrors([]);
      try {
        const res = await axios.post(`${BACKEND_URL}/api/inventory/validate-items`, {
          product_id: li.product_id, item_ids: ids
        }, getAuthHeaders());
        if (res.data.valid) {
          // Also validate amounts don't exceed remaining
          const amountErrors = [];
          for (const alloc of allocations) {
            const avail = itemModalAvailable.find(a => a.item_id === alloc.item_id);
            if (avail && avail.part_sale_remaining != null && alloc.amount > avail.part_sale_remaining) {
              amountErrors.push(`Item '${alloc.item_id}': requested ${alloc.amount} but only ${avail.part_sale_remaining} remaining`);
            }
          }
          if (amountErrors.length > 0) {
            setItemModalErrors(amountErrors);
          } else {
            setLineItems(prev => {
              const u = [...prev];
              u[itemModalIdx] = {
                ...u[itemModalIdx],
                _sold_item_ids: ids,
                _part_sale_allocations: allocations,
                _items_confirmed: true,
                part_sale_amount: qty,
              };
              return u;
            });
            setItemModalIdx(null);
          }
        } else {
          setItemModalErrors(res.data.errors || ['Validation failed']);
        }
      } catch { setItemModalErrors(['Failed to validate items']); }
      finally { setItemModalValidating(false); }
    } else {
      // Full sale: existing logic
      const ids = itemModalIds.map(s => s.trim()).filter(Boolean);
      if (ids.length !== itemModalIds.length) {
        setItemModalErrors(['All item ID fields must be filled']);
        return;
      }
      const dupes = ids.filter((v, i, a) => a.indexOf(v) !== i);
      if (dupes.length) {
        setItemModalErrors([`Duplicate item IDs: ${[...new Set(dupes)].join(', ')}`]);
        return;
      }
      setItemModalValidating(true);
      setItemModalErrors([]);
      try {
        const res = await axios.post(`${BACKEND_URL}/api/inventory/validate-items`, {
          product_id: li.product_id, item_ids: ids
        }, getAuthHeaders());
        if (res.data.valid) {
          setLineItems(prev => {
            const u = [...prev];
            u[itemModalIdx] = { ...u[itemModalIdx], _sold_item_ids: ids, _items_confirmed: true };
            return u;
          });
          setItemModalIdx(null);
        } else {
          setItemModalErrors(res.data.errors || ['Validation failed']);
        }
      } catch { setItemModalErrors(['Failed to validate items']); }
      finally { setItemModalValidating(false); }
    }
  };

  // Check if all product line items have confirmed items
  const outOfStockItems = lineItems.filter(li => !li._non_stock_product && !li._from_dc && li.product_id && (parseInt(li.quantity) || 0) > 0 && li._stock_count === 0);

  const allItemsConfirmed = lineItems.filter(li => li.product_id).every(li => {
    const qty = parseInt(li.quantity) || 0;
    if (qty <= 0) return true;
    if (li._non_stock_product) return true;
    if (li._from_dc) {
      return li._sold_item_ids.length === 0
        ? li._items_confirmed
        : li._items_confirmed && li._sold_item_ids.length === qty;
    }
    if (li._stock_count === null) return true; // no inventory tracking for this product
    if (li._stock_count === 0) return false;   // out of stock — block save
    if (li._is_part_sale) {
      return li._items_confirmed && li._part_sale_allocations.length > 0;
    }
    return li._items_confirmed && li._sold_item_ids.length === qty;
  });

  const addPayment = () => setPayments(prev => [...prev, { key: Date.now(), payment_type: 'Cash', amount: 0 }]);

  const updatePayment = (index, field, value) => {
    if (field === 'payment_type' && value === 'Credit Note') {
      setIsReceived(false);
      setReceivedAmount('');
    }
    setPayments(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removePayment = (index) => {
    if (payments.length <= 1) return;
    setPayments(prev => prev.filter((_, i) => i !== index));
  };

  // Calculations
  const subtotal = lineItems.reduce((sum, item) => sum + (item.total_amount || 0), 0);
  const roundOffAmount = roundOff ? Math.round(subtotal) - subtotal : 0;
  const total = roundOff ? Math.round(subtotal) : Math.round(subtotal * 100) / 100;
  const directReceived = isReceived ? (parseFloat(receivedAmount) || total) : 0;
  const matchingPayments = availablePayments.filter(payment =>
    payment.type === 'payment_in'
    && payment.customer_id === customerId
    && payment.company_id === companyId
    && (payment.unallocated_amount || 0) > 0
  );
  const matchingCreditNotes = availableCreditNotes.filter(note =>
    note.customer_id === customerId
    && note.company_id === companyId
    && (note.available_amount || 0) > 0
  );
  const allocatedPaymentAmount = Object.values(paymentAllocations).reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0);
  const allocatedCreditNoteAmount = Object.values(creditNoteAllocations).reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0);
  const balance = Math.round((total - directReceived - allocatedPaymentAmount - allocatedCreditNoteAmount) * 100) / 100;

  const totalQty = lineItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
  const totalDiscount = lineItems.reduce((sum, item) => sum + (item.discount_amount || 0), 0);
  const totalTax = lineItems.reduce((sum, item) => sum + (item.tax_amount || 0), 0);

  const handleSave = async () => {
    setError('');
    if (!customerId) { setError('Please select a customer'); return; }
    if (!companyId) { setError('Please select a company'); return; }
    if (!lineItems.some(li => li.product_id)) { setError('Please add at least one product'); return; }
    if (!allItemsConfirmed) {
      if (outOfStockItems.length > 0) {
        setError(`Out of stock: ${outOfStockItems.map(li => li.product_id).join(', ')} — cannot create sale invoice`);
      } else {
        setError('Please select item IDs for all products with inventory');
      }
      return;
    }
    if (directReceived + allocatedPaymentAmount + allocatedCreditNoteAmount > total + 0.001
      || Object.entries(paymentAllocations).some(([id, amount]) => {
        const payment = matchingPayments.find(item => item.id === id);
        return !payment || (parseFloat(amount) || 0) <= 0 || parseFloat(amount) > payment.unallocated_amount;
      })) {
      setError('Payment allocations must fit within the invoice balance and each payment’s available amount');
      return;
    }
    if (Object.entries(creditNoteAllocations).some(([id, amount]) => {
      const note = matchingCreditNotes.find(item => item.id === id);
      return !note || (parseFloat(amount) || 0) <= 0 || parseFloat(amount) > note.available_amount;
    })) {
      setError('Credit Note allocations must fit within the invoice balance and each note’s available amount');
      return;
    }

    // If "Including Tax" is selected, show recalculation modal instead of submitting
    if (calculateMode === 'Including Tax') {
      const preview = lineItems.filter(li => li.product_id).map(li => {
        const inclusivePrice = parseFloat(li.price_per_unit) || 0;
        const tax = taxes.find(t => t.id === li.tax_id);
        const taxPct = tax ? parseFloat(tax.percentage) : 0;
        const exclusivePrice = taxPct > 0 ? Math.round((inclusivePrice / (1 + taxPct / 100)) * 100) / 100 : inclusivePrice;
        const taxAmount = Math.round((inclusivePrice - exclusivePrice) * 100) / 100;
        const product = products.find(p => p.id === li.product_id);
        return {
          key: li.key,
          product_name: product?.name || '-',
          original_price: inclusivePrice,
          new_price: exclusivePrice,
          tax_name: tax ? `${tax.name} (${tax.percentage}%)` : 'No Tax',
          tax_pct: taxPct,
          tax_amount: taxAmount,
          quantity: parseFloat(li.quantity) || 0,
        };
      });
      setRecalcPreview(preview);
      setShowTaxRecalcModal(true);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        sale_type: saleType,
        customer_id: customerId,
        alternate_number: alternateNumber || null,
        referral_user_id: referralUserId || null,
        company_id: companyId,
        payment_term_id: paymentTermId || null,
        payment_due_date: paymentDueDate || null,
        lut_arn_number: lutArnNumber || null,
        po_number: poNumber || null,
        invoice_number: invoiceNumber,
        invoice_date: invoiceDate,
        state_of_supply: stateOfSupply,
        billing_address: billingAddress || null,
        shipping_address: shippingAddress || null,
        source_dc_id: sourceDcId || null,
        source_proforma_id: sourceProformaId || null,
        source_estimate_id: sourceEstimateId || null,
        line_items: lineItems.filter(li => li.product_id).map(li => ({
          product_id: li.product_id,
          description: li.description,
          mrp: parseFloat(li.mrp) || null,
          quantity: parseFloat(li.quantity) || 0,
          unit_id: li.unit_id || null,
          price_per_unit: parseFloat(li.price_per_unit) || 0,
          discount_percentage: parseFloat(li.discount_percentage) || 0,
          discount_amount: li.discount_amount,
          tax_id: li.tax_id || null,
          tax_amount: li.tax_amount,
          total_amount: li.total_amount,
          warranty_days: li.warranty_days ? parseInt(li.warranty_days) : null,
          warranty_id: li.warranty_id || null,
          warranty_name: li.warranty_name || null,
          part_sale_amount: (!li._is_full_sale && li.part_sale_amount) ? parseFloat(li.part_sale_amount) : null,
          sold_item_ids: li._sold_item_ids.length > 0 ? li._sold_item_ids : null,
          part_sale_allocations: li._part_sale_allocations?.length > 0 ? li._part_sale_allocations : null,
          is_full_sale: li._is_part_sale ? (li._is_full_sale || false) : null
        })),
        payments: payments.filter(p => p.payment_type !== 'Credit Note').map(p => ({
          payment_type: p.payment_type,
          amount: parseFloat(p.amount) || 0,
          bank_detail_id: p.bank_detail_id || null
        })),
        description: description || null,
        round_off: roundOff,
        round_off_amount: Math.round(roundOffAmount * 100) / 100,
        subtotal: Math.round(subtotal * 100) / 100,
        total,
        received_amount: directReceived,
        balance,
        payment_allocations: Object.entries(paymentAllocations).map(([transaction_id, allocationAmount]) => ({
          transaction_id,
          amount: parseFloat(allocationAmount) || 0,
        })),
        credit_note_allocations: Object.entries(creditNoteAllocations).map(([note_id, allocationAmount]) => ({
          note_id,
          amount: parseFloat(allocationAmount) || 0,
        }))
      };

      await axios.post(`${BACKEND_URL}/api/sale-invoices`, payload, getAuthHeaders());
      navigate('/admin/sale/invoices');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save sale invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmTaxRecalculation = () => {
    // Recalculate all line items: extract tax from inclusive price
    setLineItems(prev => {
      return prev.map(li => {
        if (!li.product_id) return li;
        const inclusivePrice = parseFloat(li.price_per_unit) || 0;
        const tax = taxes.find(t => t.id === li.tax_id);
        const taxPct = tax ? parseFloat(tax.percentage) : 0;
        const exclusivePrice = taxPct > 0 ? Math.round((inclusivePrice / (1 + taxPct / 100)) * 100) / 100 : inclusivePrice;
        const updated = { ...li, price_per_unit: exclusivePrice };
        return recalcLine(updated);
      });
    });
    setCalculateMode('Excluding Tax');
    setShowTaxRecalcModal(false);
  };

  return (
    <div data-testid="sale-invoice-create-page" className="pb-24">
      <button onClick={() => navigate('/admin/sale/invoices')} data-testid="back-btn"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to Sale Invoices
      </button>

      <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight mb-8">
        Create Sale Invoice
        {sourceDcId && <span className="text-base font-medium text-amber-600 ml-3">(from Delivery Challan)</span>}
        {sourceProformaId && <span className="text-base font-medium text-amber-600 ml-3">(from Proforma)</span>}
        {sourceEstimateId && <span className="text-base font-medium text-amber-600 ml-3">(from Estimate)</span>}
      </h1>

      {sourceProformaId && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-sm mb-6" data-testid="from-proforma-banner">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">
            Converting from Proforma Invoice{sourceProformaNumber ? <strong className="ml-1">{sourceProformaNumber}</strong> : ''}. Review the pre-filled data and click Save to create the Sale Invoice.
          </span>
        </div>
      )}

      {sourceEstimateId && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-sm mb-6" data-testid="from-estimate-banner">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">
            Converting from Estimate{sourceEstimateNumber ? <strong className="ml-1">{sourceEstimateNumber}</strong> : ''}. Review the pre-filled data and click Save to create the Sale Invoice.
          </span>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* ========== HEADER SECTION ========== */}
      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Sale Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Sale Type</label>
            <div className="flex gap-4">
              {['Cash', 'Credit'].map(type => (
                <label key={type} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="saleType" value={type} checked={saleType === type}
                    onChange={(e) => setSaleType(e.target.value)} data-testid={`sale-type-${type.toLowerCase()}`}
                    className="w-4 h-4 text-orange-500 border-slate-300" />
                  <span className="text-sm text-slate-700">{type}</span>
                </label>
              ))}
            </div>
          </div>
          {/* Customer */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Select Customer <span className="text-red-500">*</span></label>
            <ProductAutocomplete value={customerId} onChange={(id) => setCustomerId(id)} products={customers} testId="select-customer" placeholder="Search by name or phone..." />
          </div>
          {/* Alternate Number */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Alternate Number</label>
            <input type="text" value={alternateNumber}
              onChange={(e) => setAlternateNumber(e.target.value.replace(/[^0-9+\-\s]/g, ''))}
              placeholder="Alternate contact number"
              data-testid="input-alternate-number"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          {/* Referral / Lead */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Referral / Lead</label>
            <ProductAutocomplete value={referralUserId} onChange={(id) => setReferralUserId(id)} products={users} testId="select-referral" placeholder="Search user..." />
          </div>
          {/* Company */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Company <span className="text-red-500">*</span></label>
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="select-company"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
              <option value="">Select Company</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          {/* Invoice Number */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Number</label>
            <input type="text" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="e.g., INV/25-26/001"
              data-testid="input-invoice-number" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          {/* Invoice Date */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Date</label>
            <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)}
              data-testid="input-invoice-date" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          {/* State of Supply */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">State of Supply</label>
            <select value={stateOfSupply} onChange={(e) => setStateOfSupply(e.target.value)} data-testid="select-state"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
              <option value="">Select State</option>
              {STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {/* PO Number */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">PO Number</label>
            <input type="text" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="Customer PO Number"
              data-testid="input-po-number" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          {/* LUT/ARN Number */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">LUT/ARN Number</label>
            <input type="text" value={lutArnNumber} onChange={(e) => setLutArnNumber(e.target.value)} placeholder="LUT/ARN Number"
              data-testid="input-lut-arn" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          {/* Billing Address */}
          {/* Shipping Address */}
        </div>
        {/* Address Row - Full Width */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Billing Address</label>
            <textarea value={billingAddress} onChange={(e) => setBillingAddress(e.target.value)} placeholder="Billing Address" rows={3}
              data-testid="input-billing-address" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-slate-700">Shipping Address</label>
              <button type="button" onClick={() => setShippingAddress(billingAddress)}
                data-testid="copy-billing-btn" title="Copy from Billing Address"
                className="text-xs font-medium text-orange-500 hover:text-orange-600 transition-colors">
                Copy from Billing
              </button>
            </div>
            <textarea value={shippingAddress} onChange={(e) => setShippingAddress(e.target.value)} placeholder="Shipping Address" rows={3}
              data-testid="input-shipping-address" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none" />
          </div>
        </div>
        {/* Payment Terms & Calculate */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {/* Payment Terms */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Payment Terms</label>
            <select value={paymentTermId} onChange={(e) => setPaymentTermId(e.target.value)} data-testid="select-payment-term"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
              <option value="">Select Payment Terms</option>
              {paymentTerms.map(pt => <option key={pt.id} value={pt.id}>{pt.name}{pt.days ? ` (${pt.days} days)` : ''}</option>)}
            </select>
          </div>
          {/* Payment Due Date — auto-filled */}
          {paymentDueDate && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Payment Due Date</label>
              <input type="date" value={paymentDueDate} onChange={e => setPaymentDueDate(e.target.value)}
                data-testid="input-payment-due-date"
                className="w-full px-3 py-2 border border-orange-300 bg-orange-50 rounded-sm focus:border-orange-500 focus:outline-none text-orange-900" />
              <p className="text-xs text-orange-600 mt-1">
                Auto-calculated: {paymentTerms.find(pt => pt.id === paymentTermId)?.days} days from invoice date
              </p>
            </div>
          )}
          {/* Calculate Mode */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Calculate</label>
            <select value={calculateMode} onChange={(e) => setCalculateMode(e.target.value)} data-testid="select-calculate-mode"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
              <option value="Excluding Tax">Excluding Tax</option>
              <option value="Including Tax">Including Tax</option>
              <option value="None">None</option>
            </select>
          </div>
        </div>
      </div>

      {/* ========== LINE ITEMS SECTION ========== */}
      <div className="bg-white rounded-sm border border-slate-200 mb-6">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Line Items</h2>
          <button onClick={addLineItem} data-testid="add-line-item-btn"
            className="inline-flex items-center gap-1 text-orange-600 hover:text-orange-700 font-medium text-sm">
            <Plus className="h-4 w-4" /> Add Row
          </button>
        </div>

        <div className="overflow-x-auto">
          <div className="min-w-[1300px]">
            <div className="grid gap-2 px-4 py-3 border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase tracking-wider"
              style={{ gridTemplateColumns: '0.5fr 2fr 1fr 0.7fr 0.5fr 0.8fr 1fr 0.7fr 0.8fr 1fr 0.8fr 1fr 0.8fr' }}>
              <div>#</div>
              <div>Product</div>
              <div>MRP</div>
              <div>Qty</div>
              <div></div>
              <div>Unit</div>
              <div>Price/Unit</div>
              <div>Disc %</div>
              <div>Disc Amt</div>
              <div>Tax</div>
              <div>Tax Amt</div>
              <div>Amount</div>
              <div>Warranty</div>
            </div>

            {lineItems.map((item, idx) => (
              <div key={item.key} data-testid={`line-item-${idx}`}
                className="grid gap-2 px-4 py-2 border-b border-slate-100 items-start"
                style={{ gridTemplateColumns: '0.5fr 2fr 1fr 0.7fr 0.5fr 0.8fr 1fr 0.7fr 0.8fr 1fr 0.8fr 1fr 0.8fr' }}>
                <div className="flex items-center gap-1 pt-2">
                  <span className="text-slate-500 text-sm">{idx + 1}</span>
                  {lineItems.length > 1 && (
                    <button onClick={() => removeLineItem(idx)} data-testid={`remove-line-${idx}`}
                      className="p-1 text-slate-400 hover:text-red-500 transition-colors">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <div>
                  <ProductAutocomplete
                    value={item.product_id}
                    onChange={(pid) => updateLineItem(idx, 'product_id', pid)}
                    products={products}
                    disabled={item._from_dc}
                    testId={`line-product-${idx}`}
                    placeholder="Type product name..."
                  />
                  {item.product_id && item._non_stock_product && (
                    <span className="block text-xs mt-0.5 px-1 text-blue-600">Non-stock product</span>
                  )}
                  {item.product_id && !item._non_stock_product && item._stock_count !== null && (
                    <span className={`block text-xs mt-0.5 px-1 ${item._stock_count > 0 ? 'text-green-600' : 'text-red-500'}`}>
                      {item._from_dc ? 'Reserved on DC' : 'Stock'}: {item._stock_count}
                    </span>
                  )}
                  <input type="text" value={item.serial_number} onChange={(e) => updateLineItem(idx, 'serial_number', e.target.value)}
                    placeholder="Serial Number" className="w-full px-2 py-1 border border-slate-100 rounded-sm text-xs mt-1 focus:border-orange-500 focus:outline-none" />
                  <input type="text" value={item.description} onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                    placeholder="Description" className="w-full px-2 py-1 border border-slate-100 rounded-sm text-xs mt-1 focus:border-orange-500 focus:outline-none" />
                  {item._is_part_sale && !item._is_full_sale && (
                    <input type="number" value={item.part_sale_amount} onChange={(e) => updateLineItem(idx, 'part_sale_amount', e.target.value)}
                      placeholder="Part Sale Amount" min="0" step="0.01" data-testid={`line-part-sale-${idx}`}
                      className="w-full px-2 py-1 border border-orange-200 bg-orange-50 rounded-sm text-xs mt-1 focus:border-orange-500 focus:outline-none" />
                  )}
                </div>
                <div>
                  <input type="number" value={item.mrp} onChange={(e) => updateLineItem(idx, 'mrp', e.target.value)}
                    placeholder="0" className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                  {item._is_part_sale && (
                    <label className="flex items-center gap-1.5 mt-1.5 cursor-pointer">
                      <input type="checkbox" checked={item._is_full_sale} onChange={(e) => updateLineItem(idx, '_is_full_sale', e.target.checked)}
                        data-testid={`line-full-sale-${idx}`}
                        className="h-3.5 w-3.5 rounded border-slate-300 text-orange-500 focus:ring-orange-500" />
                      <span className="text-xs text-slate-600">Full Sale</span>
                    </label>
                  )}
                </div>
                <div>
                  <input type="number" value={item.quantity} onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)}
                    disabled={item._from_dc}
                    min="1" step="1" data-testid={`line-qty-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none disabled:bg-slate-100 disabled:text-slate-500" />
                </div>
                <div className="pt-1">
                  {item.product_id && !item._non_stock_product && item._stock_count > 0 && !item._from_dc && (
                    <button type="button" onClick={() => openItemModal(idx)} data-testid={`line-items-btn-${idx}`}
                      className={`p-1.5 rounded transition-colors ${item._items_confirmed ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-orange-500 bg-orange-50 hover:bg-orange-100'}`}
                      title={item._items_confirmed ? `${item._sold_item_ids.length} items selected` : 'Select items to sell'}>
                      {item._items_confirmed ? <Check className="h-4 w-4" /> : <PackagePlus className="h-4 w-4" />}
                    </button>
                  )}
                  {item.product_id && !item._non_stock_product && item._from_dc && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-700" title="Reserved by source Delivery Challan">
                      Reserved
                    </span>
                  )}
                  {item.product_id && !item._non_stock_product && item._stock_count === 0 && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-600" title="Out of stock">
                      Out of Stock
                    </span>
                  )}
                </div>
                <div>
                  <select value={item.unit_id} onChange={(e) => updateLineItem(idx, 'unit_id', e.target.value)}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none">
                    <option value="">-</option>
                    {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div>
                  <input type="number" value={item.price_per_unit} onChange={(e) => updateLineItem(idx, 'price_per_unit', e.target.value)}
                    placeholder="0" step="0.01" data-testid={`line-price-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <input type="number" value={item.discount_percentage} onChange={(e) => updateLineItem(idx, 'discount_percentage', e.target.value)}
                    min="0" max="100" step="0.01"
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <input type="number" value={item.discount_amount} onChange={(e) => updateLineItem(idx, 'discount_amount', e.target.value)}
                    min="0" step="0.01" data-testid={`line-discount-amount-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    <select value={item.tax_id} onChange={(e) => updateLineItem(idx, 'tax_id', e.target.value)}
                      data-testid={`line-tax-${idx}`}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none">
                      <option value="">-</option>
                      {taxes.map(t => <option key={t.id} value={t.id}>{t.name} ({t.percentage}%)</option>)}
                    </select>
                    {idx === 0 && item.tax_id && lineItems.filter(li => li.product_id).length > 1 && (
                      <button type="button" data-testid="apply-tax-all-btn" title="Apply this tax to all items"
                        onClick={() => setLineItems(prev => prev.map(li => li.product_id ? recalcLine({ ...li, tax_id: item.tax_id }) : li))}
                        className="shrink-0 px-2 py-1.5 text-[10px] font-bold uppercase text-orange-600 border border-orange-300 rounded-sm hover:bg-orange-50 transition-colors whitespace-nowrap">
                        All
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <span className="text-sm text-slate-600 px-2">{item.tax_amount?.toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-slate-900 px-2">{item.total_amount?.toFixed(2)}</span>
                </div>
                <div>
                  <WarrantyDropdown
                    value={item.warranty_id}
                    onChange={(wid, wname) => {
                      updateLineItem(idx, 'warranty_id', wid);
                      updateLineItem(idx, 'warranty_name', wname || '');
                    }}
                    warranties={warranties}
                    testId={`line-warranty-${idx}`}
                  />
                </div>
              </div>
            ))}

            <div className="grid gap-2 px-4 py-3 bg-slate-50 border-t border-slate-200 text-sm font-semibold"
              style={{ gridTemplateColumns: '0.5fr 2fr 1fr 0.7fr 0.5fr 0.8fr 1fr 0.7fr 0.8fr 1fr 0.8fr 1fr 0.8fr' }}>
              <div></div>
              <div className="text-slate-700">TOTAL</div>
              <div></div>
              <div className="text-slate-700">{totalQty}</div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div className="text-slate-700">{totalDiscount.toFixed(2)}</div>
              <div></div>
              <div className="text-slate-700">{totalTax.toFixed(2)}</div>
              <div className="text-slate-900">{subtotal.toFixed(2)}</div>
              <div></div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== PAYMENT & SUMMARY SECTION ========== */}
      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left — Payment & Description */}
          <div className="space-y-4">
            {payments.map((p, idx) => (
              <div key={p.key} className="flex items-center gap-3">
                <div className="flex-1 space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Payment Mode</label>
                    <select value={p.payment_type} onChange={(e) => updatePayment(idx, 'payment_type', e.target.value)}
                      data-testid={`payment-type-${idx}`}
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none text-sm">
                      <option value="">Select</option>
                      {paymentModes.filter(pm => pm.name !== 'Credit Note').map(pm => <option key={pm.id} value={pm.name}>{pm.name}</option>)}
                      <option value="Credit Note">Credit Note</option>
                    </select>
                  </div>
                  {(p.payment_type === 'Online' || p.payment_type === 'UPI') && (() => {
                    const selectedCompany = companies.find(c => c.id === companyId);
                    if (!companyId || !selectedCompany) return (
                      <p className="text-xs text-amber-600">Please select a company first to see bank accounts</p>
                    );
                    const filterType = p.payment_type === 'Online' ? 'Net Banking' : 'UPI';
                    const filtered = (selectedCompany.bank_details || []).filter(bd => bd.type === filterType);
                    if (filtered.length === 0) return (
                      <p className="text-xs text-slate-400">No {filterType} accounts added for {selectedCompany.company_name}</p>
                    );
                    return (
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">
                          {p.payment_type === 'Online' ? 'Bank Account' : 'UPI ID'}
                        </label>
                        <select value={p.bank_detail_id || ''} onChange={(e) => updatePayment(idx, 'bank_detail_id', e.target.value)}
                          data-testid={`bank-detail-${idx}`}
                          className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none text-sm">
                          <option value="">-- Select --</option>
                          {filtered.map(bd => (
                            <option key={bd.id} value={bd.id}>
                              {bd.type === 'Net Banking' ? `${bd.bank_name || ''} - ${bd.account_no || ''}` : bd.upi_id || bd.bank_name || ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })()}
                </div>
                {payments.length > 1 && (
                  <button onClick={() => removePayment(idx)} className="mt-5 p-2 text-slate-400 hover:text-red-500">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            <PaymentAllocationPicker
              payments={matchingPayments}
              allocations={paymentAllocations}
              onChange={setPaymentAllocations}
              documentBalance={Math.max(0, total - directReceived - allocatedCreditNoteAmount)}
              label="Payment In"
            />
            {useCreditNotes && (
              <NoteAllocationPicker
                notes={matchingCreditNotes}
                allocations={creditNoteAllocations}
                onChange={setCreditNoteAllocations}
                documentBalance={Math.max(0, total - directReceived - allocatedPaymentAmount)}
                label="Credit Note"
              />
            )}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Add Description</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                rows={2} placeholder="Add a note or description..."
                data-testid="input-description"
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none text-sm resize-none" />
            </div>
          </div>

          {/* Right — Summary */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <input type="checkbox" checked={roundOff} onChange={(e) => setRoundOff(e.target.checked)}
                  data-testid="round-off-checkbox" className="w-4 h-4 text-orange-500 border-slate-300 rounded" />
                <span className="text-sm text-slate-600">Round Off</span>
                {roundOff && <span className="text-sm text-slate-500">{roundOffAmount >= 0 ? '+' : ''}{roundOffAmount.toFixed(2)}</span>}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-slate-700">Total</span>
                <span className="text-lg font-bold text-slate-900 min-w-[100px] text-right" data-testid="total-amount">
                  ₹{total.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={isReceived} onChange={(e) => { setIsReceived(e.target.checked); if (e.target.checked) setReceivedAmount(String(total)); }}
                    data-testid="received-checkbox" className="w-4 h-4 text-orange-500 border-slate-300 rounded" />
                  <span className="text-sm text-slate-600">Received</span>
                </div>
                {isReceived && (
                  <input type="number" value={receivedAmount} onChange={(e) => setReceivedAmount(e.target.value)}
                    data-testid="received-amount" className="w-32 px-3 py-1.5 border border-slate-200 rounded-sm text-sm text-right focus:border-orange-500 focus:outline-none" />
                )}
              </div>
            </div>

            <div className="border-t border-slate-200 pt-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-slate-700">Balance</span>
                <span className={`text-lg font-bold min-w-[100px] text-right ${balance > 0 ? 'text-red-600' : 'text-green-600'}`}
                  data-testid="balance-amount">
                  ₹{balance.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== ACTION BUTTONS ========== */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-between z-40">
        {!allItemsConfirmed && lineItems.some(li => li.product_id) && (
          <span className="flex items-center gap-1.5 text-amber-600 text-sm"><AlertTriangle className="h-4 w-4" /> Select item IDs for all products before saving</span>
        )}
        {(allItemsConfirmed || !lineItems.some(li => li.product_id)) && <span></span>}
        <button onClick={handleSave} disabled={submitting || !allItemsConfirmed} data-testid="save-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-8 py-2.5 rounded-sm hover:bg-orange-600 transition-colors disabled:opacity-50">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save
        </button>
      </div>

      {/* ========== ITEM SELECTION MODAL ========== */}
      {itemModalIdx !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-lg my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">
                {lineItems[itemModalIdx]?._is_part_sale ? 'Allocate Item IDs (Part Sale)' : 'Select Item IDs'}
              </h2>
              <button onClick={() => setItemModalIdx(null)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-600 mb-1">
                Product: <span className="font-medium text-slate-900">{products.find(p => p.id === lineItems[itemModalIdx]?.product_id)?.name || '-'}</span>
              </p>
              <p className="text-sm text-slate-500 mb-4">
                {lineItems[itemModalIdx]?._is_part_sale
                  ? `Total qty: ${lineItems[itemModalIdx]?.quantity}. Allocate across item IDs. Available: ${itemModalAvailable.length} in stock.`
                  : `Enter ${itemModalIds.length} item ID(s) being sold. Available: ${itemModalAvailable.length} in stock.`
                }
              </p>

              {/* Show available items with remaining qty for part sale */}
              {lineItems[itemModalIdx]?._is_part_sale && itemModalAvailable.length > 0 && (
                <div className="mb-4 bg-slate-50 border border-slate-200 rounded-sm p-3 max-h-32 overflow-y-auto">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Available Items</p>
                  <div className="space-y-1">
                    {itemModalAvailable.map(a => (
                      <div key={a.item_id} className="flex justify-between text-xs">
                        <span className="text-slate-700">
                          <span className="font-mono">{a.item_id}</span>
                          <span className="ml-2 text-slate-500">Asset ID: <span className="font-mono">{a.asset_id || '-'}</span></span>
                        </span>
                        <span className={`font-medium ${(a.part_sale_remaining || 0) > 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {a.part_sale_remaining != null ? `${a.part_sale_remaining} / ${a.part_sale_full_qty} remaining` : 'Available'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {itemModalErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4 text-sm">
                  {itemModalErrors.map((e, i) => <div key={i}>{e}</div>)}
                </div>
              )}

              {lineItems[itemModalIdx]?._is_part_sale ? (
                /* Part Sale: item_id + amount allocation rows */
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {itemModalIds.map((row, i) => {
                    const [rowId, rowAmt] = row.split('||');
                    const availItem = itemModalAvailable.find(a => a.item_id === (rowId || '').trim());
                    const remaining = availItem?.part_sale_remaining;
                    return (
                      <div key={i}>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400 w-6">{i + 1}.</span>
                          <div className="flex-1 min-w-0">
                            <input
                              type="text" value={rowId || ''}
                              onChange={e => {
                                const newId = e.target.value;
                                const n = [...itemModalIds];
                                const parts = n[i].split('||');
                                parts[0] = newId;
                                // Auto-fill qty when item ID is selected
                                const matched = itemModalAvailable.find(a => a.item_id === newId.trim());
                                if (matched && matched.part_sale_remaining != null) {
                                  const itemRemaining = matched.part_sale_remaining;
                                  // Calculate how much is still needed
                                  const totalQty = parseFloat(lineItems[itemModalIdx]?.quantity) || 0;
                                  const alreadyAllocated = n.reduce((s, r, idx) => idx === i ? s : s + (parseFloat(r.split('||')[1]) || 0), 0);
                                  const stillNeeded = Math.max(totalQty - alreadyAllocated, 0);
                                  if (itemRemaining >= stillNeeded) {
                                    // Remaining covers everything needed
                                    parts[1] = String(stillNeeded);
                                  } else {
                                    // Remaining is less than needed — fill what we can and add overflow row
                                    parts[1] = String(itemRemaining);
                                    const offset = Math.round((stillNeeded - itemRemaining) * 100) / 100;
                                    if (offset > 0 && i === n.length - 1) {
                                      n.push(`||${offset}`);
                                    }
                                  }
                                }
                                n[i] = parts.join('||');
                                setItemModalIds(n);
                                setItemModalErrors([]);
                              }}
                              list={`avail-items-ps-${i}`}
                              placeholder="Item ID"
                              data-testid={`item-modal-input-${i}`}
                              className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm font-mono focus:border-orange-500 focus:outline-none"
                            />
                            <datalist id={`avail-items-ps-${i}`}>
                              {itemModalAvailable.map(a => (
                                <option key={a.item_id} value={a.item_id} label={`Asset ID: ${a.asset_id || '-'}`} />
                              ))}
                            </datalist>
                            {(rowId || '').trim() && (
                              <p className="mt-1 text-xs text-slate-500">
                                Asset ID: <span className="font-mono text-slate-700">{availItem?.asset_id || '-'}</span>
                              </p>
                            )}
                          </div>
                          <input
                            type="number" value={rowAmt || ''}
                            onChange={e => { const n = [...itemModalIds]; const parts = n[i].split('||'); parts[1] = e.target.value; n[i] = parts.join('||'); setItemModalIds(n); setItemModalErrors([]); }}
                            placeholder="Qty"
                            min="0" step="0.01"
                            data-testid={`item-modal-amount-${i}`}
                            className="w-24 px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none"
                          />
                          {itemModalIds.length > 1 && (
                            <button type="button" onClick={() => setItemModalIds(prev => prev.filter((_, j) => j !== i))}
                              className="p-1 text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                          )}
                        </div>
                        {remaining != null && (rowId || '').trim() && (
                          <div className="ml-8 text-xs text-slate-400 mt-0.5">
                            Remaining: <span className={remaining > 0 ? 'text-green-600 font-medium' : 'text-red-500 font-medium'}>{remaining}</span> / {availItem?.part_sale_full_qty}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <button type="button" data-testid="add-alloc-row-btn"
                    onClick={() => setItemModalIds(prev => [...prev, '||'])}
                    className="mt-2 text-xs text-orange-500 hover:text-orange-600 font-medium inline-flex items-center gap-1">
                    + Add another item ID
                  </button>
                  {(() => {
                    const total = itemModalIds.reduce((s, row) => s + (parseFloat(row.split('||')[1]) || 0), 0);
                    const needed = parseFloat(lineItems[itemModalIdx]?.quantity) || 0;
                    return (
                      <div className={`text-xs mt-2 ${Math.abs(total - needed) < 0.01 ? 'text-green-600' : 'text-amber-600'}`}>
                        Allocated: {total} / {needed} {Math.abs(total - needed) < 0.01 ? '(matched)' : '(must match)'}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                /* Full Sale: N item ID fields */
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {itemModalIds.map((val, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-xs text-slate-400 w-6">{i + 1}.</span>
                      <div className="flex-1 min-w-0">
                        <input
                          type="text" value={val}
                          onChange={e => { const n = [...itemModalIds]; n[i] = e.target.value; setItemModalIds(n); setItemModalErrors([]); }}
                          list={`avail-items-${i}`}
                          placeholder="Enter or select item ID"
                          data-testid={`item-modal-input-${i}`}
                          className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm font-mono focus:border-orange-500 focus:outline-none"
                        />
                        <datalist id={`avail-items-${i}`}>
                          {itemModalAvailable.map(a => (
                            <option key={a.item_id} value={a.item_id} label={`Asset ID: ${a.asset_id || '-'}`} />
                          ))}
                        </datalist>
                        {val.trim() && (
                          <p className="mt-1 text-xs text-slate-500">
                            Asset ID: <span className="font-mono text-slate-700">{itemModalAvailable.find(a => a.item_id === val.trim())?.asset_id || '-'}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-3 justify-end pt-6 mt-4 border-t border-slate-200">
                <button onClick={() => setItemModalIdx(null)} className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button onClick={validateAndConfirmItems} disabled={itemModalValidating}
                  data-testid="item-modal-confirm"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {itemModalValidating && <Loader2 className="h-4 w-4 animate-spin" />}
                  Validate & Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========== TAX RECALCULATION MODAL ========== */}
      {showTaxRecalcModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-2xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">New Calculation (Tax Inclusive → Exclusive)</h2>
              <button onClick={() => setShowTaxRecalcModal(false)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-500 mb-4">The following prices will be recalculated by extracting tax from the inclusive price:</p>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase">
                      <th className="px-3 py-2 text-left">#</th>
                      <th className="px-3 py-2 text-left">Product</th>
                      <th className="px-3 py-2 text-right">Original Price</th>
                      <th className="px-3 py-2 text-right">New Price/Unit</th>
                      <th className="px-3 py-2 text-center">Tax</th>
                      <th className="px-3 py-2 text-right">Tax Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recalcPreview.map((item, idx) => (
                      <tr key={item.key} className="border-b border-slate-100">
                        <td className="px-3 py-2 text-slate-500">{idx + 1}</td>
                        <td className="px-3 py-2 font-medium text-slate-900">{item.product_name}</td>
                        <td className="px-3 py-2 text-right text-slate-500 line-through">₹{item.original_price.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right font-bold text-slate-900">₹{item.new_price.toFixed(2)}</td>
                        <td className="px-3 py-2 text-center text-slate-700">{item.tax_name}</td>
                        <td className="px-3 py-2 text-right text-orange-600 font-medium">₹{(item.tax_amount * item.quantity).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-3 justify-end pt-4 border-t border-slate-200">
                <button onClick={() => setShowTaxRecalcModal(false)}
                  className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button onClick={confirmTaxRecalculation} data-testid="confirm-tax-recalc-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 inline-flex items-center gap-2">
                  Confirm & Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
